import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import classNames from "classnames";

import "./index.scss";
import { Box, getColor, Icons, Typography } from "fds";
import { useTranslation } from "react-i18next";
import utilsCommon from "../../shared/utils/common";
import ModalPortal from "../../shared/utils/ModalPortal";
import useWindowSize from "../../shared/hooks/useWindowSize";
import dayjs from "dayjs";
import { Policy } from "../../types/workgroup";
import AuthDotIcon from "../AuthDotIcon";
import { DocInfo, UserViewCountPolicy } from "../../types/files";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";

interface PolicyToolTipProps {
  policy?: Policy;
  buttonClick?: number;
  children: React.ReactNode;
  targetAuth?: string;
  pageType?: string;
  docInfo?: DocInfo;
  showTooltip?: boolean;
  targetViewCount?: number;
  classes?: string;
  /**
   * ADVANCED: 맞춤 권한
   * NORMAL: 기본 권한
   */
  sendingType?: "ADVANCED" | "NORMAL";
}

interface Position {
  left: number;
  top: number;
}

const PolicyToolTip: React.FC<PolicyToolTipProps> = ({
  policy,
  classes,
  children,
  targetViewCount,
  showTooltip = true,
  targetAuth,
  buttonClick,
  docInfo,
  sendingType,
  pageType,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  const { t, i18n } = useTranslation();
  const targetRef = useRef<HTMLSpanElement>(null);
  const toolTipRef = useRef<HTMLParagraphElement>(null);
  const [windowInnerWidth, windowInnerHeight] = useWindowSize();

  const [state, setState] = useState<Position>({
    left: 0,
    top: 0,
  });
  const [hover, setHover] = useState(false);
  useEffect(() => {
    setHover(false);
  }, [buttonClick]);
  useLayoutEffect(() => {
    if (!toolTipRef.current || !targetRef.current) return;
    if (!hover) return;

    const margin = 6;

    const targetRect = targetRef.current.getBoundingClientRect();
    const tooltipEl = toolTipRef.current as HTMLElement;

    // tooltip이 display:none이면 height가 0일 수 있음 → on class로 보이게 한 뒤 측정되게끔 구조 확인 필요
    const tooltipW = tooltipEl.offsetWidth;
    const tooltipH = tooltipEl.offsetHeight;

    const spaceAbove = targetRect.top;
    const spaceBelow = window.innerHeight - targetRect.bottom;

    const placeAbove =
      spaceAbove >= tooltipH + margin || spaceAbove >= spaceBelow;

    let top = placeAbove
      ? targetRect.top - tooltipH - margin
      : targetRect.bottom + margin;

    let left = targetRect.left + targetRect.width / 2 - tooltipW / 2;

    // 좌우 클램프
    left = Math.max(
      margin,
      Math.min(left, window.innerWidth - tooltipW - margin)
    );

    // 상하 클램프(안전)
    top = Math.max(
      margin,
      Math.min(top, window.innerHeight - tooltipH - margin)
    );

    setState({ left, top });
  }, [hover, windowInnerHeight, windowInnerWidth]);
  const isTooltipDisabledTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    return target.closest(`.tooltip-disable`);
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (isTooltipDisabledTarget(e.target)) return;
    setHover(true);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLSpanElement>) => {
    // 나갈 때는 그냥 끄는 게 보통 UX 좋음
    setHover(false);
  };
  const renderPolicyValue = (
    key:
      | "revision"
      | "download"
      | "wrapping"
      | "allowViewCount"
      | "allowDays"
      | "Web"
      | "Mobile"
      | "Mac"
  ) => {
    if (key === "revision") {
      return policy?.policy?.revision ? (
        <Icons
          variant="line"
          label="check"
          stroke={getColor("COLOR_ICON_BRAND")}
        />
      ) : (
        <Icons
          variant="line"
          label="closeLine"
          stroke={getColor("COLOR_ICON_ERROR")}
        />
      );
    }
    if (key === "download") {
      return policy?.policy?.download ? (
        <Icons
          variant="line"
          label="check"
          stroke={getColor("COLOR_ICON_BRAND")}
        />
      ) : (
        <Icons
          variant="line"
          label="closeLine"
          stroke={getColor("COLOR_ICON_ERROR")}
        />
      );
    }
    if (key === "wrapping") {
      return policy?.policy?.wrapping &&
        policy?.policy?.drmPerm &&
        policy?.policy?.drmPerm?.length === 0 &&
        policy.policy.download ? (
        <Box direction="column" alignItems="center">
          <Box alignItems="center">
            <Typography>{t("file.policy.normal")}</Typography>
            <Icons
              variant="line"
              label="check"
              stroke={getColor("COLOR_ICON_BRAND")}
              classes="mg ml-4"
            />
          </Box>
          <Box alignItems="center" classes="mg mt-4">
            <Typography type="body3_rg">
              {`${t("common.allow")}: ${t("file.policy.print")},${t(
                "file.policy.printScreen"
              )},${t("file.policy.copyDoc")}`}
            </Typography>
            <Icons
              variant="line"
              label="check"
              stroke={getColor("COLOR_ICON_BRAND")}
              classes="mg ml-4"
            />
          </Box>
        </Box>
      ) : policy?.policy?.wrapping && policy.policy.download ? (
        <Box direction="column" alignItems="center">
          <Box alignItems="center">
            <Typography type="body3_rg">{t("file.policy.advanced")}</Typography>
            <Icons
              variant="line"
              label="check"
              stroke={getColor("COLOR_ICON_BRAND")}
              classes="mg ml-4"
            />
          </Box>
          {policy?.policy?.drmPerm && policy?.policy?.drmPerm?.length < 4 && (
            <Box alignItems="center" classes="mg mt-4">
              <Typography type="body3_rg">
                {t("file.policy.noAllow")}
                {`${["print", "print_screen", "extract"]
                  .filter((item) => !policy.policy?.drmPerm?.includes(item))
                  .map((item1) => {
                    return item1 === "print"
                      ? t("file.policy.print")
                      : item1 === "print_screen"
                      ? t("file.policy.printScreen")
                      : item1 === "extract"
                      ? t("file.policy.copyDoc")
                      : "";
                  })
                  .join(", ")}`}
              </Typography>
              <Icons
                variant="line"
                label="check"
                stroke={getColor("COLOR_ICON_BRAND")}
                classes="mg ml-4"
              />
            </Box>
          )}
        </Box>
      ) : (
        <Icons
          variant="line"
          label="closeLine"
          stroke={getColor("COLOR_ICON_ERROR")}
        />
      );
    }
    if (key === "allowViewCount") {
      const viewCount = targetViewCount ?? policy?.policy?.currentViewCount;
      return policy?.policy?.allowViewCount === 0 || !policy?.policy ? (
        <Typography type="body3_rg">{t("file.policy.infinite")}</Typography>
      ) : (
        <Box justifyContent="center">
          <Typography type="body3_rg">
            {!!viewCount && policy?.policy?.allowViewCount - viewCount <= 0
              ? 0
              : viewCount
              ? policy?.policy?.allowViewCount - viewCount
              : policy?.policy?.allowViewCount}
          </Typography>
        </Box>
      );
    }
    if (key === "allowDays") {
      return policy?.policy?.allowDays === undefined ||
        policy?.policy?.allowDays <= 0 ? (
        <Typography whiteSpace="normal">
          {t("file.policy.noDeadline")}
        </Typography>
      ) : (
        <Box direction="column">
          <Typography whiteSpace="normal">
            {dayjs(
              policy.targetType === "USER" && sendingType !== "NORMAL"
                ? policy.policy?.startTime
                  ? policy.policy.startTime
                  : undefined
                : docInfo?.createTime ?? undefined
            )
              .add(policy?.policy?.allowDays - 1, "day")
              .format("YYYY-MM-DD")}
          </Typography>
          <Typography whiteSpace="normal">{`(GMT+${policy?.policy?.allowDaysTimeZone})`}</Typography>
        </Box>
      );
    }
    if (key === "Web") {
      return utilsCommon.checkSupportPolicy("Web") === "SUPPORT_ACCEPTED" &&
        policy?.policy?.webView &&
        loginDto.ecoCommonConfig.watermark !== "NONE" ? (
        <Typography whiteSpace="normal" wordBreak="keep-all">
          {t("file.policy.screenWatermark")}
        </Typography>
      ) : utilsCommon.checkSupportPolicy("Web") === "SUPPORT_ACCEPTED" &&
        policy?.policy?.webView ? (
        <Icons
          variant="line"
          label="check"
          stroke={getColor("COLOR_ICON_BRAND")}
        />
      ) : (
        <Icons
          variant="line"
          label="closeLine"
          stroke={getColor("COLOR_ICON_ERROR")}
        />
      );
    }
    if (key === "Mobile") {
      return utilsCommon.checkSupportPolicy("Mobile") === "SUPPORT_ACCEPTED" ? (
        <Icons
          variant="line"
          label="check"
          stroke={getColor("COLOR_ICON_BRAND")}
        />
      ) : (
        <Icons
          variant="line"
          label="closeLine"
          stroke={getColor("COLOR_ICON_ERROR")}
        />
      );
    }
    if (key === "Mac") {
      return utilsCommon.checkSupportPolicy("Mac") === "SUPPORT_ACCEPTED" ? (
        <Icons
          variant="line"
          label="check"
          stroke={getColor("COLOR_ICON_BRAND")}
        />
      ) : (
        <Icons
          variant="line"
          label="closeLine"
          stroke={getColor("COLOR_ICON_ERROR")}
        />
      );
    }
  };
  const renderTitleDotColor = (policy: Policy) => {
    return [policy.uniqueId, targetAuth].includes("MASTER")
      ? "MASTER"
      : [policy.uniqueId, targetAuth].includes("WORKGROUP") ||
        sendingType === "NORMAL"
      ? "WORKGROUP"
      : policy.authType === "DENY" || targetAuth === "NONE"
      ? "NONE"
      : "IND";
  };
  const renderToolTipTitle = (policy: Policy) => {
    return sendingType === "ADVANCED"
      ? t("sendingFile.custom") // 전송 파일 - 맞춤 권한
      : sendingType === "NORMAL"
      ? t("sendingFile.basic") // 전송파일 - 기본 권한
      : [policy.uniqueId, targetAuth].includes("MASTER")
      ? t("file.policy.master") //  관리자 권한
      : policy.authType === "DENY" || targetAuth === "NONE"
      ? t("file.policy.none") // 권한 없음
      : [policy.uniqueId, targetAuth].includes("WORKGROUP")
      ? t("file.policy.workgroup") // 워크그룹 권한)
      : t("file.policy.ind"); // 사용자 개별 권한
  };
  return (
    <React.Fragment>
      <span
        className={classNames(classes)}
        onMouseOver={handleMouseEnter}
        onMouseOut={handleMouseLeave}
        ref={targetRef}
        style={{
          display: "inline-block",
          width: "100%",
        }}
      >
        {children}
      </span>
      {policy &&
        showTooltip &&
        (!(policy.authType === "DENY" || targetAuth === "NONE") ? (
          <ModalPortal>
            <div
              className={classNames("policy-tool-tip policy-tool-tip-root", {
                on: hover,
              })}
            >
              <div
                ref={toolTipRef}
                style={{ left: `${state.left}px`, top: `${state.top}px` }}
              >
                {pageType !== "myBox" && (
                  <Box>
                    <Box
                      classes={
                        "policy-tool-tip-check-area policy-tool-tip-first"
                      }
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <AuthDotIcon auth={renderTitleDotColor(policy)} />
                    </Box>
                    <Box
                      classes={
                        "policy-tool-tip-content-area policy-tool-tip-first"
                      }
                    >
                      <Typography type="subtitle1">
                        {renderToolTipTitle(policy)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <table
                  className={
                    "tbl tbl__primary tbl__config--last tbl__fixedHeader fileNameCol"
                  }
                  width={"100%"}
                >
                  <colgroup>
                    <col width="20%"></col>
                    <col width="20%"></col>
                    <col width="20%"></col>
                    <col width="20%"></col>
                    <col width="20%"></col>
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t("file.policy.revision")}</th>
                      <th>{t("file.policy.download")}</th>
                      <th colSpan={3}>
                        <Box direction="column">
                          <Typography
                            type="body4_rg"
                            whiteSpace="break-spaces"
                            exactColor={getColor("COLOR_TEXT_SECONDARY")}
                          >
                            {t("file.policy.advanded")}
                          </Typography>
                        </Box>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{renderPolicyValue("revision")}</td>
                      <td>{renderPolicyValue("download")}</td>
                      <td colSpan={3}>{renderPolicyValue("wrapping")}</td>
                    </tr>
                  </tbody>
                </table>
                <table
                  className={
                    "tbl tbl__primary tbl__config--last tbl__fixedHeader fileNameCol"
                  }
                  width={"100%"}
                >
                  <colgroup>
                    <col width="20%"></col>
                    <col width="20%"></col>
                    <col width="20%"></col>
                    <col width="20%"></col>
                    <col width="20%"></col>
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t("file.policy.remainCount")}</th>
                      <th>{t("file.policy.allowExpiration")}</th>
                      <th>{t("file.policy.webSupport")}</th>
                      <th>{t("file.policy.mobileSupport")}</th>
                      <th>{t("file.policy.macSupport")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{renderPolicyValue("allowViewCount")}</td>
                      <td>{renderPolicyValue("allowDays")}</td>
                      <td>{renderPolicyValue("Web")}</td>
                      <td>{renderPolicyValue("Mobile")}</td>
                      <td>{renderPolicyValue("Mac")}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </ModalPortal>
        ) : (
          <ModalPortal>
            <div
              className={classNames(
                "policy-tool-tip deny policy-tool-tip-root",
                {
                  on: hover,
                }
              )}
            >
              <div
                ref={toolTipRef}
                style={{ left: `${state.left}px`, top: `${state.top}px` }}
              >
                <Box>
                  <Box
                    classes={"policy-tool-tip-check-area policy-tool-tip-first"}
                    justifyContent={"center"}
                    alignItems={"center"}
                  >
                    <AuthDotIcon auth="NONE" />
                  </Box>
                  <Box
                    classes={
                      "policy-tool-tip-content-area policy-tool-tip-first"
                    }
                  >
                    <Typography type="subtitle1">
                      {t("file.policy.none")}
                    </Typography>
                  </Box>
                </Box>
              </div>
            </div>
          </ModalPortal>
        ))}
    </React.Fragment>
  );
};

export default PolicyToolTip;
